import { useEffect, useRef } from "react";
import Footer from "@/components/Footer";

const securityBadges = [
  { value: "256 bit", label: "Full Encryption", desc: "All data encrypted at rest and in transit using AES-256 and TLS 1.3 protocols." },
  { value: "ISO 27001", label: "International certification", desc: "Independently audited information security management system (ISMS)." },
  { value: "24/7", label: "Monitoring and alerting", desc: "Continuous threat detection, logging, and incident response around the clock." },
  { value: "GDPR\u00A0Compliant", label: "Fully Compliant", desc: "Data handling practices aligned with European privacy regulations." },
];

const deploymentOptions = [
  { title: "Public Cloud", desc: "Deploy on AWS, Azure, or GCP with our managed infrastructure." },
  { title: "Private Cloud", desc: "Dedicated cloud infrastructure with enhanced isolation and security." },
  { title: "On-Premise", desc: "Behind your firewall, on your hardware, under your control." },
  { title: "Compu.J Cloud", desc: "Fully managed and optimized for wealth management workloads." },
];

const integrateLogos = [
  { src: "/assets/logos/pitchbook.svg", alt: "PitchBook" },
  { src: "/assets/logos/salesforce.svg", alt: "Salesforce" },
  { src: "/assets/logos/zoominfo.svg", alt: "ZoomInfo" },
  { src: "/assets/logos/gmail.svg", alt: "Gmail" },
  { src: "/assets/logos/sharepoint.svg", alt: "SharePoint" },
  { src: "/assets/logos/wealthdynamix.svg", alt: "Wealth Dynamix" },
  { src: "/assets/logos/bamboohr.svg", alt: "BambooHR" },
  { src: "/assets/logos/expersoft.svg", alt: "Expersoft" },
];

function useSectionReveal() {
  const ref = useRef(null);
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add("visible")),
      { threshold: 0.1 }
    );
    ref.current?.querySelectorAll(".section-reveal").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);
  return ref;
}


export default function SecurityPage() {
  const pageRef = useSectionReveal();

  return (
    <div ref={pageRef} style={{ backgroundColor: '#050508' }}>
      {/* Hero Section */}
<section
  className="pt-44 md:pt-52 pb-18 md:pb-30 px-6 md:px-12 lg:px-24"
  data-testid="security-hero"
>
  <div className="max-w-5xl">
    <h1
      className="animate-hero-title text-5xl sm:text-6xl lg:text-7xl font-medium leading-[1.05] mb-8"
      style={{ fontFamily: "'Lora', serif", color: "#F5F5F5" }}
      data-testid="security-headline"
    >
      Your data protected by enterprise-grade security
    </h1>

    <p
      className="animate-hero-sub text-base sm:text-lg lg:text-xl max-w-3xl leading-relaxed"
      style={{ color: "#A1A1AA", fontFamily: "'Lora', serif" }}
    >
      Layla is engineered to enterprise-grade security standards. Because your
      data is your business — and it should remain secure, isolated, and
      compliant by design.
    </p>
  </div>
</section>

      {/* Security Badges */}
{/* Security Badges (timeline style) */}
<section className="pt-6 pb-28 px-6 md:px-12 lg:px-24" data-testid="security-badges">
  <div className="section-reveal max-w-7xl mx-auto">
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-14 items-end">
      {securityBadges.map((badge, i) => {
        const stagger =
          i === 0
            ? "lg:translate-y-44"
            : i === 1
            ? "lg:translate-y-32"
            : i === 2
            ? "lg:translate-y-16"
            : "lg:translate-y-0";

        return (
          <div
            key={i}
            className={`relative pl-10 ${stagger} min-h-[280px]`}
            data-testid={`security-badge-${i}`}
          >
            {/* Dot */}
            <span
              aria-hidden="true"
              className="absolute left-0 top-0 h-5 w-5 rounded-full"
              style={{
                background:
                  "radial-gradient(circle at 30% 30%, #AF4AFA 0%, #7A85E2 35%, #6373FA 65%, #0013B3 100%)",
                boxShadow: "0 0 24px rgba(175,74,250,0.35)",
              }}
            />

            {/* Vertical line */}
            <span
              aria-hidden="true"
              className="absolute left-[9px] top-5 w-px h-[240px] lg:h-[280px]"
              style={{
                background:
                  "linear-gradient(to bottom, rgba(245,245,245,0.35), rgba(245,245,245,0.12), rgba(245,245,245,0))",
              }}
            />

            {/* Copy */}
            <div className="pt-1">
              <h3
  className="inline-block text-3xl md:text-4xl font-medium mb-2 gradient-white-purple"
  style={{ fontFamily: "'Lora', serif" }}
>
  {badge.value}
</h3>

              <p
                className="text-base md:text-lg font-medium mb-3"
                style={{ color: "#F5F5F5", fontFamily: "'Lora', serif" }}
              >
                {badge.label}
              </p>

              <p
                className="text-sm md:text-base leading-relaxed max-w-[18rem]"
                style={{ color: "#71717A", fontFamily: "'Lora', serif" }}
              >
                {badge.desc}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  </div>
</section>

<section
  data-testid="fd"
  className="relative pt-28 md:pt-32 pb-56 md:pb-30 px-6 md:px-12 lg:px-24 overflow-hidden"
>

</section>

{/* Flexible Deployment */}
<section
  className="relative overflow-hidden px-6 md:px-12 lg:px-24 py-32 md:py-44"
  data-testid="deployment-section"
>
  {/* WHY-US style animated background */}
<div aria-hidden="true" className="pointer-events-none absolute inset-0">
  <div className="why-orb why-glow why-orb-center" />
  <div className="why-sweep" />
</div>

  <div className="relative section-reveal max-w-7xl mx-auto">
    <h2
      className="text-5xl md:text-6xl font-medium mb-12 md:mb-16"
      style={{ fontFamily: "'Lora', serif", color: "#F5F5F5" }}
    >
      Flexible Deployment
    </h2>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
      {deploymentOptions.map((option, i) => (
        <div key={i} className="why-card px-10 py-8" data-testid={`deployment-option-${i}`}>
          <h3
            className="text-2xl md:text-3xl font-medium mb-3"
            style={{ fontFamily: "'Lora', serif", color: "#F5F5F5" }}
          >
            {option.title}
          </h3>

          <p
            className="text-base md:text-lg leading-relaxed"
            style={{ fontFamily: "'Lora', serif", color: "#A1A1AA" }}
          >
            {option.desc}
          </p>
        </div>
      ))}
    </div>
  </div>
</section>


      {/* How We Build (Figma tile layout) */}
<section
  className="relative overflow-hidden py-24 md:py-32 px-6 md:px-12 lg:px-24"
  data-testid="how-we-build"
>
  {/* subtle background glow like Figma */}
  <div aria-hidden="true" className="pointer-events-none absolute inset-0">
    <div className="why-orb why-glow why-orb-center opacity-40" />
  </div>

  <div className="relative section-reveal max-w-6xl mx-auto">
    <h2
      className="text-4xl md:text-5xl font-medium mb-10 md:mb-12"
      style={{ fontFamily: "'Lora', serif", color: "#F5F5F5" }}
    >
      How we build
    </h2>

    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 lg:gap-8">
      {/* Discover (accent) */}
      <div className="hwb-card" data-testid="how-we-build-discover">
        <div className="hwb-inner">
          <div>
            <h3 className="hwb-title">Discover</h3>
            <p className="hwb-desc">
              We begin by identifying the real-world challenges in your workflows — no one-size-fits-all,
              just a clear view of what matters most.
            </p>
          </div>
          <div className="hwb-figure">
          </div>
        </div>
      </div>

      {/* Design */}
      <div className="hwb-card" data-testid="how-we-build-design">
        <div className="hwb-inner">
          <div>
            <h3 className="hwb-title">Design</h3>
            <p className="hwb-desc">
              We architect solutions tailored to your operating model — workflows, roles, approvals, and
              controls — so the platform fits how your firm actually works.
            </p>
          </div>
          <div className="hwb-figure hwb-figure-empty" />
        </div>
      </div>

   
      {/* Integrate (ONE tile) */}
<div className="hwb-card sm:col-span-2" data-testid="how-we-build-integrate">
  <div className="hwb-inner hwb-inner-integrate">
    <div>
      <h3 className="hwb-title">Integrate</h3>
      <p className="hwb-desc">
        We unify your existing systems and data sources into a centralised dashboard.
      </p>
    </div>

    <div className="hwb-logos-panel">
      <img
        src={`${process.env.PUBLIC_URL}/assets/integration-logos.png`}
        alt="Integration partners"
        className="hwb-logos-img"
      />
    </div>
  </div>
</div>

      {/* Deploy */}
      <div className="hwb-card" data-testid="how-we-build-deploy">
        <div className="hwb-inner">
          <div>
            <h3 className="hwb-title">Deploy</h3>
            <p className="hwb-desc">
              We deploy where it works best — your private cloud, an enterprise provider, or the Compu.J
              cloud — so you stay in full control of your data.
            </p>
          </div>
          <div className="hwb-figure hwb-figure-empty" />
        </div>
      </div>

      {/* Evolve */}
      <div className="hwb-card" data-testid="how-we-build-evolve">
        <div className="hwb-inner">
          <div>
            <h3 className="hwb-title">Evolve</h3>
            <p className="hwb-desc">
              As your business grows, your platform adapts. Our modular architecture makes continuous
              improvement seamless.
            </p>
          </div>
          <div className="hwb-figure">
          </div>
        </div>
      </div>
    </div>
  </div>
</section>

<section
  data-testid="join team"
  className="relative pt-8 md:pt-32 pb-36 md:pb-32 px-6 md:px-12 lg:px-24 overflow-hidden"
>

</section>
      {/* Join Our Team */}
      <section className="pt-14 pb-70 px-6 md:px-12 lg:px-24" data-testid="join-team">
        <div className="section-reveal max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div>
              <h2
  className="text-5xl md:text-6xl font-medium mb-6"
  style={{ fontFamily: "'Lora', serif", color: "#F5F5F5" }}
>
  Join Our Team
</h2>

<p
  className="text-base md:text-lg mb-4 leading-relaxed max-w-xl"
  style={{ color: "#A1A1AA", fontFamily: "'Lora', serif" }}
>
  We're always looking for exceptional individuals to strengthen our delivery and client impact.
</p>

<p
  className="text-base md:text-lg leading-relaxed max-w-xl"
  style={{ color: "#A1A1AA", fontFamily: "'Lora', serif" }}
>
  If you value autonomy, integrity, and purpose-driven work — and want to shape how AI transforms
  financial services — we'd like to hear from you. Reach out to us on careers@compu-j.com
</p>
            </div>
            <div className="rounded-xl overflow-hidden">
              <img src="/assets/careers-team.png" alt="Join Our Team" className="w-full h-64 md:h-80 object-cover" />
            </div>
          </div>
        </div>
      </section>

<section
  data-testid="final-cta"
  className="relative pt-28 md:pt-32 pb-[184px] md:pb-[200px] px-6 md:px-12 lg:px-24 overflow-hidden"
></section>
      <Footer />
    </div>
  );
}
